// Types matching CORE / CRM project schemas

export interface Listing {
  id: string;
  code: string;
  client_code: string | null;
  pricelabs_id: string | null;
  property_id: string | null;
  push_enabled: boolean | null;
  nickname: string | null;
  
  source: ReservationSource | null;
}

/** Derive effective client_code from a listing (CRM property.client_code is authoritative) */
export function deriveClientCode(l: Listing): string | null {
  return l.client_code ?? null;
}

// pg_reservation

export interface Reservation {
  id: string;
  created_at: string;
  listing: string;
  booked_date: string;
  reservation_id: string;
  check_in: string;
  check_out: string;
  booking_status: string;
  rental_revenue: number;
  no_of_days: number;
  currency: string;
  total_cost_bup: number;
  blt: number | null;
  cancelled_on: string | null;
  property_id: string | null;
  pricelabs_id?: string | null;
  source?: string | null;
  confirmation_code?: string | null;
  system_source?: "guesty" | "pricelabs" | null;
}

// pg_monthly_kpis
export interface MonthlyKPI {
  id: number;
  listing_id: string | null;
  start_date: string | null;
  kpi_occupancy_rate: number | null;
  kpi_market_occupancy_rate: number | null;
}

export type DateMode = "stay" | "booked";

export type QuickFilter =
  | "today"
  | "last7" | "last15" | "last30" | "last90"
  | "next30" | "next60" | "next90"
  | "allTime"
  | "custom";

export type ReservationSource = "pricelabs" | "guesty";
export type ViewMode = "table" | "calendar";

export interface FilterState {
  dateMode: DateMode;
  quickFilter: QuickFilter;
  dateFrom: Date | null;
  dateTo: Date | null;
  listings: string[];
  clients: string[];
  bookingStatuses: string[];
  includeCancellations: boolean;
  search: string;
  listingStatus: "all" | "listed" | "unlisted";
}

export interface KPIValues {
  reservations: number;
  roomNights: number;
  revenue: number;
  adr: number;
  avgLos: number;
  cancelRate: number;
  avgLeadTime: number;
  totalCostBup: number;
}

export interface SavedView {
  id: string;
  name: string;
  filters: FilterState;
  createdAt: string;
}

export type SortDirection = "asc" | "desc";
export interface SortConfig {
  key: string;
  direction: SortDirection;
}

// pg_daily_kpis

export interface DailyKPI {
  id: number;
  listing_id: string | null;
  date: string | null;
  booking_status: string | null;
  booking_status_stly: string | null;
  price: number | null;
  min_stay: number | null;
  adr: number | null;
  adr_stly: number | null;
  kpi_market_occupancy_rate: number | null;
  kpi_market_occupancy_rate_stly: number | null;
  kpi_market_occupancy_rate_ly: number | null;
  kpi_market_occupancy_rate_pickup_7: number | null;
  kpi_occupancy_pickup_7: number | null;
  kpi_occupancy_pickup_stly_7: number | null;
  kpi_adr_pickup_7: number | null;
  kpi_adr_stly_pickup_7: number | null;
  created_at: string | null;
  last_synced_at: string;
  min_price: number | null;
  kpi_los: number | null;
  kpi_blt: number | null;
  property_code: string | null;
  kpi_adr_reservations_stly: number | null;
  booked_date: string | null;
}

// Helper: compute ADR safely
export function computeADR(revenue: number, nights: number): number {
  if (!nights || nights === 0) return 0;
  return revenue / nights;
}

// Helper: compute lead time in days
export function computeLeadTime(booked_date: string, check_in: string): number {
  const booked = new Date(booked_date);
  const checkin = new Date(check_in);
  const diff = checkin.getTime() - booked.getTime();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}
